import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Modal, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { PoolCredential } from './networkApi'

const ROOT = 'xsib-credential-print'
const COLUMNS = 3
const ROWS = 8
const PER_PAGE = COLUMNS * ROWS
const SLIP_WIDTH_MM = 70
const SLIP_HEIGHT_MM = 37

const PRINT_CSS = `
  .${ROOT} { display: none; }
  @media print {
    @page { size: A4 portrait; margin: 0; }
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    body > *:not(.${ROOT}) { display: none !important; }
    .${ROOT} { display: block; }
    .xsib-credential-page { break-after: page; page-break-after: always; }
    .xsib-credential-page:last-child { break-after: auto; page-break-after: auto; }
  }
`

interface Heading {
  visp: string
  center: string
}

export function CredentialSheet({
  credentials,
  heading,
  onClose,
}: {
  credentials: readonly PoolCredential[] | null
  heading: Heading
  onClose: () => void
}) {
  const { t } = useTranslation()
  const pages = paginate(credentials ?? [])

  return (
    <Modal
      open={credentials !== null}
      title={t('network.visps.credentialsTitle', { name: heading.visp })}
      okText={t('network.visps.print')}
      cancelText={t('common.close')}
      onOk={() => window.print()}
      onCancel={onClose}
      okButtonProps={{ disabled: pages.length === 0 }}
      width={860}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="warning" showIcon message={t('network.visps.credentialsWarn')} />
        {pages.length === 0 ? (
          <Typography.Text type="secondary">{t('network.visps.noAvailable')}</Typography.Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ boxShadow: '0 0 0 1px #d9d9d9', width: '210mm' }}>
              <Page slips={pages[0] ?? []} heading={heading} />
            </div>
          </div>
        )}
      </Space>
      {pages.length > 0 ? <PrintPages pages={pages} heading={heading} /> : null}
    </Modal>
  )
}

function PrintPages({ pages, heading }: { pages: PoolCredential[][]; heading: Heading }) {
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={ROOT}>
      {pages.map((slips) => (
        <Page key={slips[0]?.id} slips={slips} heading={heading} />
      ))}
    </div>,
    document.body,
  )
}

function Page({ slips, heading }: { slips: readonly PoolCredential[]; heading: Heading }) {
  return (
    <div
      className="xsib-credential-page"
      style={{
        width: '210mm',
        height: '297mm',
        boxSizing: 'border-box',
        padding: `${(297 - ROWS * SLIP_HEIGHT_MM) / 2}mm ${(210 - COLUMNS * SLIP_WIDTH_MM) / 2}mm`,
        display: 'grid',
        gridTemplateColumns: `repeat(${COLUMNS}, ${SLIP_WIDTH_MM}mm)`,
        gridAutoRows: `${SLIP_HEIGHT_MM}mm`,
        background: '#fff',
        color: '#000',
      }}
    >
      {slips.map((slip) => (
        <Slip key={slip.id} slip={slip} heading={heading} />
      ))}
    </div>
  )
}

function Slip({ slip, heading }: { slip: PoolCredential; heading: Heading }) {
  const { t } = useTranslation()
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontWeight: 700 } as const
  return (
    <div
      style={{
        boxSizing: 'border-box',
        border: '0.2mm dashed #888',
        padding: '3mm',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5mm',
        fontSize: '3mm',
        overflow: 'hidden',
      }}
    >
      <span dir="auto" style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {heading.center} / {heading.visp}
      </span>
      <span>
        {t('network.visps.credentialLogin')}: <bdi dir="ltr" style={{ ...mono, fontSize: '4mm' }}>{slip.login}</bdi>
      </span>
      <span>
        {t('network.visps.credentialPassword')}:{' '}
        <bdi dir="ltr" style={{ ...mono, fontSize: '4mm', letterSpacing: '0.05em' }}>{slip.password}</bdi>
      </span>
    </div>
  )
}

function paginate(slips: readonly PoolCredential[]): PoolCredential[][] {
  const pages: PoolCredential[][] = []
  for (let i = 0; i < slips.length; i += PER_PAGE) pages.push(slips.slice(i, i + PER_PAGE))
  return pages
}
